/**
 * What a job's matching-limits state means to the person looking at it.
 *
 * avrio-backend (2026-10-02) saves every job, but only sends it to matching
 * while the customer is within its monthly limits. A job past them is
 * "paused", and resumes on its own at the start of the next UTC month or as
 * soon as Avrio raises the limit. Both pause reasons read the same on purpose:
 * the shared estate-wide ceiling is never disclosed to a customer.
 *
 * A job with no matchingStatus predates matching limits and gets no notice.
 */
import { Job } from '../../../fsm/jobs';

export interface MatchingNotice {
  tone: 'warning' | 'error' | 'info';
  /** Short, for a badge in the job list. */
  badge?: string;
  title: string;
  detail?: string;
}

const PAUSED = new Set(['PAUSED_JOB_CAP', 'PAUSED_GLOBAL_CEILING']);
export const RESUMED_NEW_MONTH = 'Resumed from last month';
export const RESUMED_LIMIT_RAISED = 'Resumed: limit raised';

export const isMatchingPaused = (job?: Pick<Job, 'matchingStatus'> | null) =>
  !!job?.matchingStatus && PAUSED.has(job.matchingStatus);

/** The first day of the UTC month after `now`, as a date a person reads. */
export const nextMonthStart = (now: Date = new Date()) =>
  new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)));

const day = (iso?: string | null) =>
  iso
    ? new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
      }).format(new Date(iso))
    : null;

export const matchingNotice = (
  job?: Pick<
    Job,
    'matchingStatus' | 'matchingStatusReason' | 'matchingStatusUpdatedAt'
  > | null,
  now: Date = new Date(),
): MatchingNotice | null => {
  if (!job?.matchingStatus) return null;
  if (isMatchingPaused(job)) {
    return {
      tone: 'warning',
      badge: 'Matching paused',
      title: 'Matching paused: monthly limit reached',
      detail: `This job is saved. Matching starts on its own on ${nextMonthStart(
        now,
      )}, or sooner if Avrio raises your limit.`,
    };
  }
  if (job.matchingStatus === 'MATCHING_FAILED') {
    return {
      tone: 'error',
      badge: 'Matching not started',
      title: 'Matching could not start',
      detail: 'Save the job again to retry.',
    };
  }
  const when = day(job.matchingStatusUpdatedAt);
  if (job.matchingStatusReason === RESUMED_NEW_MONTH) {
    return {
      tone: 'info',
      title: when ? `Matching resumed ${when}` : 'Matching resumed',
      detail: 'This job was paused last month at your monthly limit.',
    };
  }
  if (job.matchingStatusReason === RESUMED_LIMIT_RAISED) {
    return {
      tone: 'info',
      title: when ? `Matching resumed ${when}` : 'Matching resumed',
      detail: 'Avrio raised your monthly limit.',
    };
  }
  return null;
};
